import ImageWithLoader from "../Commaon/ImageWithLoader";
import { formatDateTime } from "../../utils/date";

export const STATUS_FILTERS = ["Open", "Processing", "Solved", "Cancelled"];
export const ADMIN_STATUSES = ["Open", "Processing", "Solved"];
export const MAX_SUGGESTION_ITEMS = 20;

const STATUS_BADGE = {
  Open: "bg-primary",
  Processing: "bg-warning text-dark",
  Solved: "bg-success",
  Cancelled: "bg-secondary",
};

export const StatusBadge = ({ status }) => (
  <span className={`badge ${STATUS_BADGE[status] || "bg-light text-dark"}`}>{status || "-"}</span>
);

export const isCancelled = (item) => item?.status === "Cancelled";

export const getFiles = (item) => (Array.isArray(item?.files) ? item.files.filter((file) => file?.url) : []);

/** Files can be deleted only once the prescription is Solved/Cancelled and still has files. */
export const canDeleteFiles = (item) =>
  ["Solved", "Cancelled"].includes(item?.status) && !item?.filesDeletedAt && getFiles(item).length > 0;

export const deleteFilesBlockedReason = (item) => {
  if (item?.filesDeletedAt || !getFiles(item).length) return "Files are already removed";
  if (!["Solved", "Cancelled"].includes(item?.status)) {
    return "Files can be deleted only after the prescription is Solved or Cancelled";
  }
  return "";
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** "2 Nov" style short date. */
export const formatShortDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

export const suggestionSummary = (suggestion) => {
  if (!suggestion) return "-";
  const count = suggestion.items?.length || 0;
  const what = count ? plural(count, "item") : "Note only";
  const sent = formatShortDate(suggestion.sentAt);
  return sent ? `${what} · sent ${sent}` : what;
};

export const memberLabel = (member) => {
  if (!member) return "-";
  return member.relation ? `${member.fullName || "-"} (${member.relation})` : member.fullName || "-";
};

const byLabel = (by) => (by === "admin" ? "admin" : by === "user" ? "user" : by || "system");

/** Human readable text for one activity log entry. */
export const describeActivity = (entry) => {
  const details = entry?.details || {};
  const by = byLabel(entry?.by);
  switch (entry?.action) {
    case "uploaded":
      return `Uploaded ${plural(details.fileCount || 0, "file")} by ${by}`;
    case "status_changed":
      return `Status ${details.from || "-"} → ${details.to || "-"} by ${by}`;
    case "suggestion_sent":
      return `Suggestion sent (${plural(details.itemCount || 0, "item")}) by ${by}`;
    case "cancelled":
      return `Cancelled by ${by}`;
    case "files_deleted":
      return `${details.fileCount ? plural(details.fileCount, "file") : "Files"} deleted by ${by}`;
    case "added_to_cart":
      return `${by === "user" ? "User" : by} added ${plural(details.itemCount || 0, "item")} to cart`;
    default:
      return `${String(entry?.action || "Activity").replace(/_/g, " ")} by ${by}`;
  }
};

export const ActivityTimeline = ({ activity }) => {
  const entries = Array.isArray(activity) ? [...activity] : [];
  if (!entries.length) return <div className="text-muted small">No activity yet</div>;
  entries.sort((a, b) => new Date(b.at) - new Date(a.at));
  return (
    <ul className="list-unstyled mb-0">
      {entries.map((entry, index) => (
        <li key={`${entry.action}-${entry.at}-${index}`} className="d-flex gap-2 pb-2 mb-2 border-bottom">
          <span
            className={`rounded-circle flex-shrink-0 mt-1 ${entry.by === "admin" ? "bg-primary" : "bg-success"}`}
            style={{ width: "8px", height: "8px" }}
          />
          <div>
            <div className="small">{describeActivity(entry)}</div>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              {formatDateTime(entry.at)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Compact list cell: up to 3 thumbnails / PDF chips, or "Files removed". */
export const FilesCell = ({ item }) => {
  const files = getFiles(item);
  if (item?.filesDeletedAt) return <span className="badge bg-light text-muted border">Files removed</span>;
  if (!files.length) return <span className="text-muted">No files</span>;
  return (
    <div className="d-flex flex-wrap align-items-center gap-1">
      {files.slice(0, 3).map((file, index) =>
        file.type === "pdf" ? (
          <a
            key={`${file.url}-${index}`}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="badge bg-danger-subtle text-danger border border-danger-subtle text-decoration-none"
            title={file.name || "PDF"}
          >
            PDF
          </a>
        ) : (
          <a key={`${file.url}-${index}`} href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
            <ImageWithLoader
              src={file.url}
              alt={file.name || `File ${index + 1}`}
              style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
            />
          </a>
        )
      )}
      {files.length > 3 && <span className="text-muted small">+{files.length - 3}</span>}
    </div>
  );
};
